import React from 'react'
import styled, { css } from 'styled-components'
import { useHistory } from 'react-router-dom'
import { Text, Block, Spinner, Button, H2 } from '@mote/ui'
import {
  moteConversationDataGetter,
  useMoteConversationMutation,
  useTrackMutation
} from '@mote/api'
import { ChevronDownIcon } from '@mote/assets/icons'
import { MotePlayerProviders } from '../shared/components'
import { WebRecorder } from '../recorder/views/web/WebRecorder'
import { Error } from '../recorder/views/web/icons'
import { Notice } from '../recorder/views/shared/Notice'
import { getAccessToken } from 'web/src/shared/Auth'
import { useRecordReplySidebar } from './RecordReplySidebar.context'
import { Playback } from './moteReply/Playback'
import { MoteRepliesList } from './moteRepliesList'

export const RecordReplySidebar = () => {
  const [mounted, setMounted] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [loadingPollingLimit, setPollingPastLimit] = React.useState(false)
  const [playbackMote, setPlaybackMote] = React.useState(null)
  const [refetchConversation, setRefetchConversation] = React.useState(false)
  const [registeredMote, setRegisteredMote] = React.useState(null)
  const cancelPollingTimeoutRef = React.useRef(null)
  const [error, setError] = React.useState(null)
  const { mutate: track } = useTrackMutation()

  const { state, toggle, addNewReply, removeLastReply } =
    useRecordReplySidebar()
  const history = useHistory()

  const { data, mutate: fetchConversation } = useMoteConversationMutation(
    state.mote?.parentMote.moteId,
    refetchConversation ? 2000 : false
  )

  const onRecorderDone = React.useCallback(() => {
    fetchConversation()
    setRefetchConversation(true)
  }, [fetchConversation])

  const handleMoteStopRecording = React.useCallback(mote => {
    if (!mote) return
    setLoading(true)
    setRegisteredMote(mote)

    cancelPollingTimeoutRef.current = setTimeout(() => {
      setPollingPastLimit(true)

      cancelPollingTimeoutRef.current = setTimeout(() => {
        setLoading(false)
        setPollingPastLimit(false)
        setRegisteredMote(null)
        setRefetchConversation(false)
        setError('POLLING_LIMIT_EXCEEDED')
        // TODO: SET DIAGNOSTICS TO CAPTURE THIS
      }, 15000)
    }, 15000)
  }, [])

  const renderRecorder = React.useMemo(() => {
    return (
      <>
        {!playbackMote && (
          <HiddenElement hidden={loading}>
            <WebRecorder
              parentMoteId={state?.mote?.parentMote.moteId}
              onFinalize={mote => {
                if (!mote) return
                onRecorderDone()
              }}
              onStop={handleMoteStopRecording}
            />
          </HiddenElement>
        )}

        {playbackMote && (
          <HiddenElement hidden={loading}>
            <MotePlayerProviders>
              <Playback
                moteId={playbackMote.moteId}
                audioUrl={playbackMote.playbackLocation}
                onReset={() => {
                  setPlaybackMote(null)
                }}
                onDelete={() => {
                  setPlaybackMote(null)
                  removeLastReply()
                }}
              />
            </MotePlayerProviders>
          </HiddenElement>
        )}
      </>
    )
  }, [
    loading,
    playbackMote,
    state,
    handleMoteStopRecording,
    removeLastReply,
    onRecorderDone,
    removeLastReply
  ])

  const conversationData = React.useMemo(() => {
    if (!data) return null

    return moteConversationDataGetter(data)
  }, [data])

  const renderLoading = React.useMemo(
    () => (
      <Block align='center' justify='center' gap={32}>
        <Spinner radius={100} color='#AC0AE8' duration={1400} strokeWidth={3} />
        <Text
          fontSize={20}
          fontWeight={500}
          lineHeight='140%'
          color='#232323'
          alignment='center'
        >
          {!loadingPollingLimit && 'Thinking...'}
          {loadingPollingLimit && 'This is taking a bit longer...'}
        </Text>
      </Block>
    ),
    [loadingPollingLimit]
  )

  React.useEffect(() => {
    setMounted(true)
  }, [])

  React.useEffect(() => {
    if (!conversationData || !conversationData.replyMotes || !registeredMote)
      return

    const newlyCreatedMote = conversationData.replyMotes.find(
      mote => mote.moteId === registeredMote.moteId
    )

    if (newlyCreatedMote) {
      clearTimeout(cancelPollingTimeoutRef.current)
      cancelPollingTimeoutRef.current = null

      const newReply =
        conversationData.replyMotes[conversationData.replyMotes.length - 1]

      setPlaybackMote(newReply)
      setRefetchConversation(false)
      setLoading(false)
      setPollingPastLimit(false)
      addNewReply(newReply)

      track({
        variables: {
          event: 'Mote Created Audio',
          content_asset_id: newReply.moteId,
          is_reply_mote: true,
          asset_length: newReply.duration,
          creator_cohort: conversationData.parentMote.labs.labsCohort
        }
      })
    }
  }, [conversationData, registeredMote])

  if (!mounted) return null

  return (
    <>
      <Header>
        <CloseButton
          aria-label='Close sidebar'
          icon={<ChevronRightIcon />}
          onClick={toggle}
          variant='ghost'
        />

        <ReplyingTo
          fontSize={18}
          fontWeight={500}
          lineHeight='120%'
          color='#232323'
          alignment='center'
        >
          Reply to {state.mote?.parentMote.createdByGreeting}
        </ReplyingTo>

        {getAccessToken() && !error && renderRecorder}
        {!getAccessToken() && (
          <LoginPrompt align='center' justify='center' spreadX spreadY>
            <LoginButton
              aria-label='Login to reply'
              text='Login to reply'
              onClick={() =>
                history.push(
                  `/login?force=1&returnTo=${window.location.pathname}`
                )
              }
            />
          </LoginPrompt>
        )}

        {loading && !error && renderLoading}

        {error && (
          <Notice
            Icon={Error}
            title='Oh no!'
            description='Something went wrong with your recording. Please try again or kindly contact support.'
            cta={{ action: () => setError(null) }}
          />
        )}
      </Header>

      <ReplyingToContainer>
        <MoteRepliesList
          replies={state.mote?.replyMotes}
          isOpen={state.isOpen}
        />
      </ReplyingToContainer>
    </>
  )
}

const Header = styled.header`
  position: relative;
  display: flex;
  background: #f9f9fb;
  padding: 24px;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`

const ReplyingToContainer = styled.div`
  position: relative;
  padding: 0 24px 24px 24px;
  height: 100%;
  min-height: -webkit-fill-available;
`

const ReplyingTo = styled(H2)``

const HiddenElement = styled.div`
  ${({ hidden }) =>
    hidden
      ? css`
          display: none;
        `
      : css`
          display: block;
        `};
`

const LoginPrompt = styled(Block)``

const LoginButton = styled(Button)``

const CloseButton = styled(Button)`
  position: absolute;
  top: 22px;
  left: 24px;
  padding: 4px;
  height: 24px;
  width: 24px;
`

const ChevronRightIcon = styled(ChevronDownIcon)`
  transform: rotate(90deg);
`
